import React, { useCallback, useMemo } from 'react'
import {
  type AdornmentIcon,
  CopyNotify,
  type ExtraSchema,
  FieldTypes,
  MaskInput,
  useFieldGroup
} from '@bcraft-frontend-lib/payments'
import { TextFieldProps } from '@mui/material'
import block from 'bem-cn'
import { FormikErrors } from 'formik'

import { AutoCompleteComponent, DatePicker, FormInput, Input, TimePicker } from 'components/UIKit'

import './PaymentFieldGroup.scss'

type IPaymentField = TextFieldProps & {
  field: ExtraSchema
  title: string
  value: string | number
  touched: Record<string, boolean | undefined>
  errors: FormikErrors<Record<string, string | number>>
  setFieldValue: (field: string, value: string) => void
}

const b = block('payment-field-group')

export const PaymentFieldGroup: React.FC<IPaymentField> = props => {
  const { field, title, value, touched, errors, onBlur, setFieldValue } = props

  const onChangeValue = useCallback((v: string) => setFieldValue(field.name, v), [field.name, setFieldValue])

  const fieldProps = useFieldGroup({ field, title, errors, onChangeValue })

  const getAdornmentContent = useMemo(
    () => (a?: AdornmentIcon) => {
      const onAdornmentClick = () => {
        a?.callback?.({ value })
      }
      const adornmentBlock = a?.icon && (
        <>
          <img alt="" src={a?.icon} className={b('adornment', { name: a.name })} onClick={onAdornmentClick} />
          <CopyNotify isCopied={fieldProps.isCopied} />
        </>
      )

      return {
        endAdornment: a?.type === 'end' && adornmentBlock,
        startAdornment: a?.type === 'start' && adornmentBlock,
        adornment: adornmentBlock,
      }
    },
    [fieldProps.isCopied, value],
  )

  const filedByType = useMemo(() => {
    switch (fieldProps.fieldType) {
      case FieldTypes.INPUT: {
        const { endAdornment } = getAdornmentContent(fieldProps.adornment)
        const inputProps = {
          type: fieldProps.type,
          onChange: fieldProps.setValue,
          isError: Boolean(touched[field.name] && fieldProps.error),
          errorText: Boolean(touched[field.name]) && fieldProps.error,
          placeholder: fieldProps.placeholder,
          disabled: fieldProps.disabled,
          spellCheck: false,
          suffix: endAdornment,
          ...(fieldProps.adornment?.name === 'password' ? {
            canBeHidden: true,
            initialHidden: !fieldProps.initialVisible
          } : {}),
        }

        return (
          <FormInput
            name={field.name}
            {...inputProps}
          // caption={fieldProps.label}
          />
        )
      }
      case FieldTypes.SELECTOR: {
        return (
          <div className={b('autocomplete')}>
            <AutoCompleteComponent
              data={fieldProps.data}
              value={String(value)}
              defaultValue={String(value)}
              onSelect={fieldProps.setValue}
            />
          </div>
        )
      }

      case FieldTypes.MASK_INPUT: {
        const { endAdornment } = getAdornmentContent(fieldProps.adornment)
        const inputProps = {
          type: fieldProps.type,
          isError: Boolean(touched[field.name] && fieldProps.error),
          errorText: Boolean(touched[field.name]) && fieldProps.error,
          placeholder: fieldProps.placeholder,
          disabled: fieldProps.disabled,
          spellCheck: false,
          ...(fieldProps.adornment?.name === 'password' ? {
            canBeHidden: true,
            initialHidden: !fieldProps.initialVisible
          } : {}),
        }

        return (
          <MaskInput
            {...fieldProps}
            value={String(value)}
            onChangeValue={onChangeValue}
            patternSource={fieldProps.pattern}
            maxLength={fieldProps.maxLength}>
            {({ setValue, value: formattedValue }) => (
              <Input
                name={field.name}
                suffix={endAdornment}
                onChange={setValue}
                onBlur={onBlur}
                value={formattedValue}
                {...inputProps}
              // caption={fieldProps.label}
              />
            )}
          </MaskInput>
        )
      }

      case FieldTypes.DATE_PICKER: {
        return (
          <div className={b('date')}>
            <div className={b('date-input')}>
              <DatePicker
                caption={fieldProps.label}
                value={new Date(value)}
                onChange={v => fieldProps.setDateValue(v, value.toString())}
              />
            </div>
            <div className={b('date-input')}>
              <TimePicker
                callback={v => fieldProps.setTimeValue(v, value.toString())}
                format={fieldProps.timeFormatRequest}
                defaultValue={new Date(value)}
                calendarDefaultDate={new Date(value)}
                size="md"
                block
              />
            </div>
          </div>
        )
      }

      default:
        return null
    }
  }, [field.name, fieldProps, getAdornmentContent, onBlur, onChangeValue, touched, value])

  if (filedByType === null) return null
  return (
    <div className={b()} key={field.name}>
      {filedByType}
    </div>
  )
}
